import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Crown, Server, Globe, ArrowUpRight, Loader2, AlertTriangle, Shield, RefreshCw, Zap } from 'lucide-react';

interface Subscription {
    plan: string;
    role: string;
    expired: boolean;
    expiresAt: string | null;
    limits: { servers: number | string; domains: number | string };
    usage: { servers: number; domains: number };
    remaining: { servers: number | string; domains: number | string };
}

const ProfilePanel: React.FC = () => {
    const { user } = useAuth();
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => { loadSubscription(); }, []);

    const loadSubscription = async () => {
        try {
            setLoading(true);
            const res = await fetch('/api/auth/subscription', { credentials: 'include' });
            const data = await res.json();
            if (data.success) setSubscription(data.subscription);
            else setError(data.error || 'Failed to load');
        } catch { setError('Failed to load subscription'); }
        finally { setLoading(false); }
    };

    const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : 'Never';
    const getUsagePercent = (used: number, limit: number | string) => typeof limit === 'string' ? 5 : Math.min(100, (used / limit) * 100);

    const planName = subscription?.plan || 'Free';
    const isAdmin = planName === 'Admin';

    if (loading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
            </div>
        );
    }

    return (
        <div className="w-full min-h-full animate-fade-in">
            {/* Profile Hero */}
            <div className="relative rounded-xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/10 p-8 mb-6 overflow-hidden">
                {/* Background accent */}
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 blur-3xl" style={{ backgroundColor: 'var(--accent-primary)' }} />

                <div className="relative flex items-start justify-between">
                    <div className="flex items-center gap-6">
                        {/* Large Avatar */}
                        <div
                            className="w-24 h-24 rounded-full flex items-center justify-center text-4xl font-bold shadow-xl border-2 border-white/10"
                            style={{
                                background: `linear-gradient(135deg, var(--accent-primary), var(--accent-primary-rgb) ? rgba(var(--accent-primary-rgb), 0.5) : var(--accent-primary))`,
                                color: 'white'
                            }}
                        >
                            {user?.username?.charAt(0).toUpperCase()}
                        </div>

                        {/* User Info */}
                        <div>
                            <h1 className="text-3xl font-bold text-white mb-2">{user?.username}</h1>
                            <div className="flex items-center gap-3">
                                <span
                                    className="px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5"
                                    style={{
                                        backgroundColor: 'rgba(var(--accent-primary-rgb, 139, 92, 246), 0.15)',
                                        color: 'var(--accent-primary)'
                                    }}
                                >
                                    {isAdmin && <Shield className="w-4 h-4" />}
                                    {planName === 'Pro' && <Crown className="w-4 h-4" />}
                                    {planName === 'Enterprise' && <Crown className="w-4 h-4" />}
                                    {planName === 'Starter' && <Zap className="w-4 h-4" />}
                                    {planName}
                                </span>
                                {!isAdmin && (
                                    <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${subscription?.expired ? 'bg-red-500/15 text-red-400' : 'bg-green-500/15 text-green-400'}`}>
                                        {subscription?.expired ? 'Expired' : 'Active'}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        <button onClick={loadSubscription} className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors" title="Refresh">
                            <RefreshCw className="w-5 h-5" />
                        </button>
                        {(planName === 'Free' || planName === 'Starter' || subscription?.expired) && (
                            <a
                                href="https://ikcore.cloud/pricing"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors"
                            >
                                <Crown className="w-4 h-4" />
                                Upgrade
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400">{error}</span>
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                    {/* Account Details */}
                    <div className="rounded-xl bg-white/[0.02] border border-white/10 p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-white/40" />
                            Account Details
                        </h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between py-3 border-b border-white/5">
                                <span className="text-white/50 text-sm">Username</span>
                                <span className="text-white font-medium">{user?.username}</span>
                            </div>
                            <div className="flex items-center justify-between py-3 border-b border-white/5">
                                <span className="text-white/50 text-sm">Role</span>
                                <span className="text-white capitalize">{user?.role}</span>
                            </div>
                            <div className="flex items-center justify-between py-3 border-b border-white/5">
                                <span className="text-white/50 text-sm">Member since</span>
                                <span className="text-white">{formatDate(user?.created_at || null)}</span>
                            </div>
                            {!isAdmin && subscription?.expiresAt && (
                                <div className="flex items-center justify-between py-3 border-b border-white/5">
                                    <span className="text-white/50 text-sm">Subscription expires</span>
                                    <span className={subscription.expired ? 'text-red-400 font-medium' : 'text-white'}>{formatDate(subscription.expiresAt)}</span>
                                </div>
                            )}
                            <div className="flex items-center justify-between py-3">
                                <span className="text-white/50 text-sm">Last login</span>
                                <span className="text-white">{formatDate(user?.last_login || null)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Plan Features */}
                    <div className="rounded-xl bg-white/[0.02] border border-white/10 p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Zap className="w-4 h-4 text-yellow-400" />
                            Plan Features
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-2 text-white/70 text-sm">
                                <div className="w-2 h-2 rounded-full bg-green-400" />
                                <span>{subscription?.limits.servers} servers</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/70 text-sm">
                                <div className="w-2 h-2 rounded-full bg-green-400" />
                                <span>{subscription?.limits.domains} domains</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/70 text-sm">
                                <div className="w-2 h-2 rounded-full bg-green-400" />
                                <span>Page generator</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/70 text-sm">
                                <div className="w-2 h-2 rounded-full bg-green-400" />
                                <span>Server monitoring</span>
                            </div>
                            {(planName === 'Pro' || planName === 'Enterprise' || isAdmin) && (
                                <>
                                    <div className="flex items-center gap-2 text-white/70 text-sm">
                                        <div className="w-2 h-2 rounded-full bg-green-400" />
                                        <span>Telegram bots</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-white/70 text-sm">
                                        <div className="w-2 h-2 rounded-full bg-green-400" />
                                        <span>Browser check</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - Usage */}
                <div className="space-y-6">
                    {/* Servers Usage */}
                    <div className="rounded-xl bg-white/[0.02] border border-white/10 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                    <Server className="w-5 h-5 text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-white font-medium">Servers</p>
                                    <p className="text-white/40 text-xs">Resource usage</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-bold text-white">{subscription?.usage.servers}</p>
                                <p className="text-white/40 text-xs">of {subscription?.limits.servers}</p>
                            </div>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${getUsagePercent(subscription?.usage.servers || 0, subscription?.limits.servers || 1)}%` }} />
                        </div>
                        <p className="text-xs text-white/30 mt-2">{subscription?.remaining.servers} slots available</p>
                    </div>

                    {/* Domains Usage */}
                    <div className="rounded-xl bg-white/[0.02] border border-white/10 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(var(--accent-primary-rgb, 139, 92, 246), 0.1)' }}>
                                    <Globe className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                                </div>
                                <div>
                                    <p className="text-white font-medium">Domains</p>
                                    <p className="text-white/40 text-xs">Resource usage</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-bold text-white">{subscription?.usage.domains}</p>
                                <p className="text-white/40 text-xs">of {subscription?.limits.domains}</p>
                            </div>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${getUsagePercent(subscription?.usage.domains || 0, subscription?.limits.domains || 1)}%`, backgroundColor: 'var(--accent-primary)' }} />
                        </div>
                        <p className="text-xs text-white/30 mt-2">{subscription?.remaining.domains} slots available</p>
                    </div>

                    {/* Quick Stats */}
                    <div className="rounded-xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/10 p-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-4 rounded-lg bg-white/[0.02]">
                                <p className="text-3xl font-bold text-white">{subscription?.usage.servers}</p>
                                <p className="text-white/40 text-xs mt-1">Active Servers</p>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-white/[0.02]">
                                <p className="text-3xl font-bold text-white">{subscription?.usage.domains}</p>
                                <p className="text-white/40 text-xs mt-1">Active Domains</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePanel;
